// ShareModal Component - Universal sharing functionality
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Share2, Search, X, User, Building2, Send, Check, 
  Copy, Twitter, Facebook, MessageCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ShareModal = ({ isOpen, onClose, itemType, itemId, itemTitle, itemData }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [shareType, setShareType] = useState('users'); // users, social

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const searchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users/search?query=${encodeURIComponent(searchQuery)}&limit=10`);
      setSearchResults(res.data);
    } catch (e) {
      console.error('Search error:', e);
    }
  };

  const toggleUser = (user) => {
    if (selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const shareWithUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Selecciona al menos un usuario');
      return;
    }
    
    setLoading(true);
    try {
      for (const user of selectedUsers) {
        await axios.post(`${API}/share?user_id=${itemData?.userId}`, {
          item_type: itemType,
          item_id: itemId,
          shared_with_type: 'user',
          shared_with_identifier: user.okuser,
          message: message
        });
      }
      toast.success(`Compartido con ${selectedUsers.length} usuario(s)`);
      onClose();
    } catch (e) {
      toast.error('Error al compartir');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/shared/${itemType}/${itemId}`;
    navigator.clipboard.writeText(link);
    toast.success('Enlace copiado');
  };

  const shareToSocial = (platform) => {
    const text = `¡Mira esto en OkBot: ${itemTitle}`;
    const url = `${window.location.origin}/shared/${itemType}/${itemId}`;
    
    const socialUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`
    };
    
    window.open(socialUrls[platform], '_blank', 'width=600,height=400');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="glass-panel w-full max-w-md p-6 animate-fade-in-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Share2 className="text-red-400" size={20} />
            </div>
            <div>
              <h3 className="text-white font-semibold">Compartir</h3>
              <p className="text-gray-500 text-sm truncate max-w-[200px]">{itemTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="text-gray-400" size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button 
            onClick={() => setShareType('users')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              shareType === 'users' ? 'bg-red-500 text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
            }`}>
            <User size={14} className="inline mr-2" />Usuarios
          </button>
          <button 
            onClick={() => setShareType('social')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              shareType === 'social' ? 'bg-red-500 text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
            }`}>
            <Share2 size={14} className="inline mr-2" />Redes
          </button>
        </div>

        {shareType === 'users' ? (
          <>
            {/* Search Users */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por nombre o @usuario..."
                className="input-glass pl-10 w-full"
              />
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mb-4 max-h-40 overflow-y-auto space-y-1">
                {searchResults.map(user => (
                  <button
                    key={user.id}
                    onClick={() => toggleUser(user)}
                    className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                      selectedUsers.find(u => u.id === user.id)
                        ? 'bg-red-500/20 border border-red-500/50'
                        : 'bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {user.name.charAt(0)}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-white text-sm font-medium">{user.name}</p>
                      <p className="text-gray-500 text-xs">@{user.okuser}</p>
                    </div>
                    {selectedUsers.find(u => u.id === user.id) && (
                      <Check className="text-red-400" size={18} />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Selected Users */}
            {selectedUsers.length > 0 && (
              <div className="mb-4">
                <p className="text-gray-500 text-xs mb-2">Seleccionados ({selectedUsers.length}):</p>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map(user => (
                    <span key={user.id} className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm flex items-center gap-2">
                      @{user.okuser}
                      <button onClick={() => toggleUser(user)} className="hover:text-red-300">
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <div className="mb-4">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Añade un mensaje (opcional)..."
                className="input-glass w-full h-20 resize-none"
              />
            </div>

            {/* Share Button */}
            <button
              onClick={shareWithUsers}
              disabled={loading || selectedUsers.length === 0}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? 'Compartiendo...' : (
                <>
                  <Send size={18} />
                  Compartir
                </>
              )}
            </button>
          </>
        ) : (
          <>
            {/* Social Sharing */}
            <div className="space-y-3 mb-4">
              <button
                onClick={() => shareToSocial('whatsapp')}
                className="w-full p-4 bg-green-500/20 hover:bg-green-500/30 rounded-xl flex items-center gap-4 transition-colors"
              >
                <MessageCircle className="text-green-400" size={24} />
                <span className="text-white font-medium">WhatsApp</span>
              </button>
              <button
                onClick={() => shareToSocial('twitter')}
                className="w-full p-4 bg-blue-500/20 hover:bg-blue-500/30 rounded-xl flex items-center gap-4 transition-colors"
              >
                <Twitter className="text-blue-400" size={24} />
                <span className="text-white font-medium">Twitter / X</span>
              </button>
              <button
                onClick={() => shareToSocial('facebook')}
                className="w-full p-4 bg-blue-600/20 hover:bg-blue-600/30 rounded-xl flex items-center gap-4 transition-colors"
              >
                <Facebook className="text-blue-500" size={24} />
                <span className="text-white font-medium">Facebook</span>
              </button>
            </div>

            {/* Copy Link */}
            <button
              onClick={copyLink}
              className="w-full p-4 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center gap-3 transition-colors"
            >
              <Copy className="text-gray-400" size={20} />
              <span className="text-white">Copiar enlace</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ShareModal;
